import { ClickAwayListener, Grid, useMediaQuery } from "@mui/material";
import {
  useClickAway,
  useIntersectionObserver,
  useWindowScroll,
} from "@uidotdev/usehooks";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";
import Color from "../../../assets/colors";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import useTriggerSnackbar from "../../../hooks/useTriggerSnackbar";
import {
  bookmarkCompany,
  getIndustries,
  unBookmarkCompany,
  updateSelectedCompany,
} from "../../../redux/actions/company_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../redux/actions/user_action";
import {
  CompanyNameRow,
  CompanyVerifiedBadge,
  TooltipWrapper,
} from "../../jobs/AccordionJobCard/styles";
import VerifiedCompanyDialog from "../CompanyNavigation/VerifiedCompanyDialog";
import DropCv from "../DropCv/DropCv";
import {
  CircularProgressStyled,
  CompanyLogo,
  FlexContainer,
  IndustryIconStyled,
  JobIconStyled,
  TextStyled,
} from "../styles";
import {
  ClipboardContainer,
  CompanyDetailWrapper,
  CompanyName,
  CompanyVerifiedBadgeLarge,
  FilledButton,
  FollowButton,
  FollowIconStyled,
  FollowingIconStyled,
  InfoCirleFilledStyled,
  LinkIconStyled,
  NavigationMainRow,
  NavigationMainRowContent,
  NavigationWrapper,
  NoBoldStyled,
  ShareButton,
  ShareButtonsContainer,
  ShareIconStyled,
  StyledTooltip,
  StyledTooltipText,
  TabStyled,
  TabsContainer,
  TooltipInfoText,
  VerifiedBadgeButton,
  WorkHereButton,
  WorkHereIconStyled,
} from "./styles";

function CompanyNavigation(props) {
  const { companyDetail, currentTab, setCurrentTab, companySocialExist } =
    props || {};

  const navRef = useRef(null);

  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isTablet = useMediaQuery("(min-width: 640px)");
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [verifiedDialogOpen, setVerifiedDialogOpen] = useState(false);
  const [dropCvDialogOpen, setDropCvDialogOpen] = useState(false);
  const [resumeSent, setResumeSent] = useState(false);

  useEffect(() => {
    setResumeSent(companyDetail?.companyApplicationSubmitted === true);
  }, [companyDetail?.id, companyDetail?.companyApplicationSubmitted]);

  const id = companyDetail?.id;

  const fetchingCompany = useSelector(
    (state) => state?.companies?.fetchingCompany
  );
  const bookmarking = useSelector(
    (state) => state.companies?.bookmarkingCompany
  );
  const unbookmarking = useSelector(
    (state) => state.companies?.unbookmarkCompany
  );
  const bookmark = useSelector(
    (state) => state.companies?.selectedCompany?.bookmark
  );
  const industriesList = useSelector((state) => state.companies.industries);

  const currentCompanyIndustry = industriesList?.filter(
    (industry) => industry.title == companyDetail?.industry
  );
  const industryLabel = useMemo(() => {
    if (!companyDetail?.industry && !companyDetail?.industryId) return "";
    const keyBase = companyDetail?.industry;
    if (!keyBase) return "";
    const key = keyBase
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companies.industry_labels.${key}`);
  }, [companyDetail?.industry, companyDetail?.industryId, industriesList, t]);

  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    dispatch(getIndustries());
  }, [dispatch]);

  useEffect(() => {
    if (!fetchingCompany && bookmark !== null) {
      setShowButton(true);
    }
  }, [bookmark]);

  const [ref, entry] = useIntersectionObserver({
    threshold: 0,
    rootMargin: "-100px 0px 0px 0px",
  });
  const showDiv = entry?.isIntersecting;

  useEffect(() => {
    if (showDiv) setCurrentTab(1);
  }, [showDiv, setCurrentTab]);

  const [{ y }, scrollTo] = useWindowScroll();

  const shareRef = useClickAway(() => {
    setShareIconsOpen(false);
  });

  const jobCount = companyDetail?.jobCount;
  const videoCount = companyDetail?.videoCount;
  const benefitsCount = companyDetail?.benefitsCount;

  const tabItems = companySocialExist
    ? [
        {
          name: "about",
          label: t("companydetail.tab_about"),
          value: 2,
          id: "cdp-tab-about",
        },
        {
          name: "office",
          label: t("companydetail.tab_office"),
          value: 1,
          id: "cdp-tab-office",
        },
        {
          name: "people",
          label: t("companydetail.tab_people"),
          value: 3,
          id: "cdp-tab-people",
        },
        {
          name: "benefits",
          label: t("companydetail.tab_benefits"),
          value: 4,
          id: "cdp-tab-benefits",
        },
        {
          name: "more",
          label: t("companydetail.tab_more"),
          value: 5,
          id: "cdp-tab-more",
        },
      ]
    : [
        {
          name: "office",
          label: t("companydetail.tab_office"),
          value: 1,
          id: "cdp-tab-office",
        },
        {
          name: "about",
          label: t("companydetail.tab_about"),
          value: 2,
          id: "cdp-tab-about",
        },
        {
          name: "people",
          label: t("companydetail.tab_people"),
          value: 3,
          id: "cdp-tab-people",
        },
        {
          name: "benefits",
          label: t("companydetail.tab_benefits"),
          value: 4,
          id: "cdp-tab-benefits",
        },
        {
          name: "more",
          label: t("companydetail.tab_more"),
          value: 5,
          id: "cdp-tab-more",
        },
      ];

  let filteredTab = [...tabItems]; // Create a copy of tabItems

  if (jobCount === 0) {
    // Remove the element with name "more"
    filteredTab = filteredTab.filter((item) => item.name !== "more");
  }

  if (videoCount === 0) {
    // Remove the element with name "benefits"
    filteredTab = filteredTab.filter((item) => item.name !== "people");
  }

  if (benefitsCount === 0) {
    // Remove the element with name "benefits"
    filteredTab = filteredTab.filter((item) => item.name !== "benefits");
  }

  function handleScrollTop() {
    scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleScrollToJob() {
    sendTrackingEvent({
      event: "CE_click-view-button-jobs-cdp",
      "company-id": id,
    });

    const headerOffset = 200;
    const elementPosition = document
      .getElementById("CompanyJobs")
      ?.getBoundingClientRect().top;
    const offsetPosition = elementPosition + y - headerOffset;
    scrollTo({ top: offsetPosition, behavior: "smooth" });
  }

  function getHeaderOffset() {
    const navEl = navRef.current;
    if (!navEl) return 0;

    const rect = navEl.getBoundingClientRect();
    // offset value from the bottom of the sticky nav
    const ADDITIONAL_OFFSET = 40;

    // dynamically calculate the bottom position of the nav no matter its height
    return rect.bottom + ADDITIONAL_OFFSET;
  }

  function handleScrollTo(newTab) {
    let section;

    switch (newTab) {
      case 1:
        section = document.getElementById("CompanyOffice");
        handleEventTab("office");
        break;
      case 2:
        section = document.getElementById("CompanyAbout");
        handleEventTab("about");
        break;
      case 3:
        section = document.getElementById("CompanyPeople");
        handleEventTab("people");
        break;
      case 4:
        section = document.getElementById("CompanyBenefits");
        handleEventTab("benefits");
        break;
      case 5:
        section = document.getElementById("CompanyJobs");
        handleEventTab("more");
        break;
      default:
        section = document.getElementById("CompanyOffice");
        handleEventTab("office");
    }

    const headerOffset = getHeaderOffset();
    const elementPosition = section?.getBoundingClientRect().top;
    const offsetPosition = elementPosition + y - headerOffset;
    scrollTo({ top: offsetPosition, behavior: "smooth" });
    setCurrentTab(newTab);
  }

  function handleEventTab(cTab) {
    sendTrackingEvent({
      event: "CE_click-company-view-tab",
      "company-id": companyDetail?.id,
      section: cTab,
    });
  }

  function handleFollow() {
    const loggedIn = checkAuthentication();

    if (!loggedIn) {
      return dispatch(updateSelectedCompany(companyDetail)).then(() => {
        dispatch(updateSignUpOrigin("save_company")).then(() => {
          dispatch(updateShowSignInModalStatus(true));
        });
      });
    }

    if (!bookmark) {
      sendTrackingEvent({
        event: "CE_follow-company-cdp",
        "company-id": id,
      });

      dispatch(bookmarkCompany({ companyId: id }));
    } else if (bookmark) {
      sendTrackingEvent({
        event: "CE_unfollow-company-cdp",
        "company-id": id,
      });

      dispatch(unBookmarkCompany({ companyId: id }));
    }
  }

  const [shareIconsOpen, setShareIconsOpen] = useState(false);

  function handleShare() {
    sendTrackingEvent({
      event: "CE_click-share-cdp",
      "company-id": id,
    });
    setShareIconsOpen(!shareIconsOpen);
  }

  function handleOpenDropCvDialog() {
    setDropCvDialogOpen(true);
  }

  function handleCloseDropCvDialog() {
    setDropCvDialogOpen(false);
  }

  function handleIndustryFilterHref() {
    let industrySlug = "";

    currentCompanyIndustry?.forEach((industry) => {
      industrySlug += "industry_ids=" + industry?.id + "&";
    });

    return (
      "/companies?" +
      industrySlug.slice(0, industrySlug.length - 1) +
      "&triggerOrigin=cdp-text"
    );
  }

  function TooltipChip() {
    return (
      <ClickAwayListener
        onClickAway={() => {
          setTooltipOpen(false);
        }}
      >
        <StyledTooltip
          open={tooltipOpen}
          onClick={() => {
            setTooltipOpen(true);
          }}
          onMouseEnter={() => {
            setTooltipOpen(true);
          }}
          onClose={() => {
            setTooltipOpen(false);
          }}
          componentsProps={{
            tooltip: {
              sx: {
                display: "flex",
                width: "234px",
                padding: "10px 14px",
                flexDirection: "column",
                alignItems: "center",
                borderRadius: "12px",
                border: "2px solid #222222",
                background: "#FFF",
              },
            },
          }}
          sx={{
            display: "flex !important",
            gap: "4px",
          }}
          title={
            <TooltipInfoText>
              <Trans
                i18nKey="companydetail.recruitment_tooltip"
                values={{ company: companyDetail?.name }}
                components={[<span style={{ fontWeight: 700 }} />]}
              />
            </TooltipInfoText>
          }
          placement="bottom-start"
        >
          <StyledTooltipText>
            {t("companydetail.recruitment_firm_label")}
          </StyledTooltipText>
          <InfoCirleFilledStyled />
        </StyledTooltip>
      </ClickAwayListener>
    );
  }

  const isCompanyVerified =
    companyDetail?.verified === true ||
    companyDetail?.name === "Hiredly" ||
    companyDetail?.name === "Hiredly X";

  const handleOpenVerifiedDialog = () => {
    setVerifiedDialogOpen(true);
  };

  const handleCloseVerifiedDialog = () => {
    setVerifiedDialogOpen(false);
  };

  return (
    <>
      <div ref={ref}></div>
      <NavigationWrapper ref={navRef} smallView={!showDiv && isTablet}>
        <NavigationMainRow>
          <NavigationMainRowContent>
            <FlexContainer
              alignItems={"center"}
              gap={
                showDiv
                  ? isTablet
                    ? "1.5rem"
                    : "1rem"
                  : isDesktop
                  ? "0.5rem"
                  : "0"
              }
            >
              {companyDetail.logo ? (
                showDiv ? (
                  <CompanyLogo
                    src={companyDetail?.logo}
                    alt={t("companydetail.company_logo_alt", {
                      company: companyDetail?.name,
                    })}
                    aria-label={t("companydetail.company_logo_aria")}
                    id="company-logo"
                    height={"65px"}
                    width={"65px"}
                    style={{ cursor: "pointer" }}
                    onClick={handleScrollTop}
                  />
                ) : isDesktop ? (
                  <CompanyLogo
                    src={companyDetail?.logo}
                    alt={t("companydetail.company_logo_alt", {
                      company: companyDetail?.name,
                    })}
                    aria-label={t("companydetail.company_logo_aria")}
                    id="company-logo"
                    height={"48px"}
                    width={"48px"}
                    style={{ cursor: "pointer" }}
                    onClick={handleScrollTop}
                  />
                ) : (
                  <CompanyLogo
                    src={companyDetail?.logo}
                    alt={t("companydetail.company_logo_alt", {
                      company: companyDetail?.name,
                    })}
                    aria-label={t("companydetail.company_logo_aria")}
                    id="company-logo"
                    height={"30px"}
                    width={"0px"}
                    style={{ border: "none" }}
                    onClick={handleScrollTop}
                  />
                )
              ) : null}

              <CompanyDetailWrapper $showDiv={showDiv}>
                <Grid
                  display={"flex"}
                  alignItems={isDesktop ? "center" : "flex-start"}
                  gap={"8px"}
                  sx={{ minWidth: 0 }}
                >
                  <CompanyNameRow
                    sx={{
                      flex: "1 1 auto",
                      minWidth: 0,
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <CompanyName id="company-name">
                      {companyDetail?.name}
                    </CompanyName>
                    {isCompanyVerified &&
                      (isDesktop ? (
                        <TooltipWrapper
                          followCursor
                          title={t("companies.verified_company_tooltip")}
                        >
                          <VerifiedBadgeButton
                            type="button"
                            aria-label="Click to view details"
                            onClick={handleOpenVerifiedDialog}
                          >
                            <CompanyVerifiedBadgeLarge aria-label="Verified company" />
                          </VerifiedBadgeButton>
                        </TooltipWrapper>
                      ) : (
                        <TooltipWrapper
                          followCursor
                          title={t("companies.verified_company_tooltip")}
                        >
                          <VerifiedBadgeButton
                            type="button"
                            aria-label="Click to view details"
                            onClick={handleOpenVerifiedDialog}
                          >
                            <CompanyVerifiedBadge aria-label="Verified company" />
                          </VerifiedBadgeButton>
                        </TooltipWrapper>
                      ))}
                  </CompanyNameRow>
                  {companyDetail?.recruitmentFirm && <TooltipChip />}
                </Grid>
                <FlexContainer gap={"0.75rem"}>
                  <Link
                    href={handleIndustryFilterHref()}
                    style={{ textDecoration: "none", color: "currentcolor" }}
                  >
                    <FlexContainer
                      alignItems={"center"}
                      gap={"0.5rem"}
                      sx={{ width: "auto", cursor: "pointer" }}
                      id="company-industry"
                    >
                      <IndustryIconStyled />
                      <TextStyled
                        fontFamily={"Karla"}
                        fontSize={"0.875rem"}
                        lineHeight={"1.25rem"}
                        letterSpacing={"0.15px"}
                        whiteSpace={"nowrap"}
                      >
                        {industryLabel}
                      </TextStyled>
                    </FlexContainer>
                  </Link>
                  {companyDetail?.jobCount > 0 ? (
                    <FlexContainer
                      alignItems={"center"}
                      gap={"0.5rem"}
                      color={Color?.hiredlyPurple}
                      onClick={handleScrollToJob}
                      sx={{ width: "auto", cursor: "pointer" }}
                      id="company-job-count"
                    >
                      <JobIconStyled />
                      <TextStyled
                        fontFamily={"Karla"}
                        fontSize={"0.875rem"}
                        lineHeight={"1.25rem"}
                        letterSpacing={"0.15px"}
                      >
                        {t("companydetail.jobs_count", {
                          count: companyDetail?.jobCount,
                        })}
                      </TextStyled>
                    </FlexContainer>
                  ) : null}
                </FlexContainer>
              </CompanyDetailWrapper>

              {showButton ? (
                <FlexContainer
                  justifyContent={!isTablet && "space-between"}
                  gap={!isTablet && "0.5rem"}
                  sx={{ display: showDiv && "none" }}
                  alignSelf={!isTablet && "stretch"}
                  padding={!isTablet && "8px"}
                >
                  <TabsContainer>
                    {filteredTab?.map((item, index) => (
                      <TabStyled
                        key={index}
                        onClick={() => handleScrollTo(item?.value)}
                        selected={currentTab === item?.value}
                        id={item?.id}
                      >
                        {item?.label}
                      </TabStyled>
                    ))}
                  </TabsContainer>

                  {!isTablet && companyDetail?.jobCount > 0 ? (
                    <FilledButton
                      aria-label={t("companydetail.view_jobs_aria")}
                      onClick={handleScrollToJob}
                      style={{
                        borderRadius: "0.375rem",
                        height: "2.5rem",
                        width: "fit-content",
                        padding: "0 0.625rem",
                      }}
                      id="company-view-jobs"
                    >
                      <JobIconStyled />
                      <NoBoldStyled>
                        {"(" + companyDetail?.jobCount + ")"}
                      </NoBoldStyled>
                    </FilledButton>
                  ) : null}
                </FlexContainer>
              ) : null}
            </FlexContainer>

            {showButton ? (
              <FlexContainer
                gap={"0.5rem"}
                sx={{
                  display: !isTablet && !showDiv && "none",
                  width: isTablet ? "fit-content" : "100%",
                  alignSelf: !isTablet && "stretch",
                }}
              >
                {companyDetail?.jobCount > 0 ? (
                  <FilledButton
                    aria-label={t("companydetail.view_jobs_aria")}
                    onClick={handleScrollToJob}
                    id="company-view-jobs"
                  >
                    {t("companydetail.view_jobs")}
                    <NoBoldStyled>
                      {"(" + companyDetail?.jobCount + ")"}
                    </NoBoldStyled>
                  </FilledButton>
                ) : null}
                {companyDetail?.companyApplicationEnabled ? (
                  <WorkHereButton
                    type="button"
                    aria-label={t("companydetail.work_here_aria")}
                    id="cdp-work-here"
                    onClick={handleOpenDropCvDialog}
                    sx={
                      resumeSent
                        ? {
                            color: Color.white,
                            backgroundColor: Color.black,
                            borderColor: Color.black,
                            "&:hover": {
                              color: Color.white,
                              backgroundColor: Color.black,
                            },
                          }
                        : undefined
                    }
                  >
                    {!resumeSent && <WorkHereIconStyled />}
                    {t(
                      resumeSent
                        ? "companydetail.work_here_sent"
                        : "companydetail.work_here"
                    )}
                  </WorkHereButton>
                ) : (
                  <WorkHereButton
                    type="button"
                    aria-label={t("companydetail.follow_company_aria")}
                    id="cdp-follow"
                    onClick={handleFollow}
                    disabled={bookmarking || unbookmarking}
                    sx={
                      bookmark
                        ? {
                            color: Color.white,
                            backgroundColor: Color.black,
                            borderColor: Color.black,
                          }
                        : undefined
                    }
                  >
                    {bookmarking || unbookmarking ? (
                      <CircularProgressStyled />
                    ) : (
                      t(
                        bookmark
                          ? "companydetail.following"
                          : "companydetail.follow"
                      )
                    )}
                  </WorkHereButton>
                )}
                <div ref={shareRef}>
                  <ShareButton
                    aria-label={t("companydetail.share_company_aria")}
                    onClick={handleShare}
                    id="cdp-share"
                  >
                    <ShareIconStyled />
                  </ShareButton>

                  {shareIconsOpen ? (
                    <SharePopup
                      companyDetail={companyDetail}
                      showDiv={showDiv}
                    />
                  ) : null}
                </div>
                {companyDetail?.companyApplicationEnabled ? (
                  <FollowButton
                    type="button"
                    aria-label={t("companydetail.follow_company_aria")}
                    onClick={handleFollow}
                    disabled={bookmarking || unbookmarking}
                    sx={
                      bookmark && {
                        backgroundColor: Color?.black,
                        color: Color?.white,
                      }
                    }
                    id="cdp-follow"
                  >
                    {bookmarking || unbookmarking ? (
                      <CircularProgressStyled />
                    ) : bookmark ? (
                      <FollowingIconStyled />
                    ) : (
                      <FollowIconStyled />
                    )}
                  </FollowButton>
                ) : null}
              </FlexContainer>
            ) : null}
          </NavigationMainRowContent>
        </NavigationMainRow>
      </NavigationWrapper>
      <VerifiedCompanyDialog
        open={verifiedDialogOpen}
        onClose={handleCloseVerifiedDialog}
        companyDetail={companyDetail}
        onMoreAboutCompany={() => {
          handleCloseVerifiedDialog();
          handleScrollTo(2);
        }}
      />
      <DropCv
        open={dropCvDialogOpen}
        onClose={handleCloseDropCvDialog}
        companyId={id}
        onSubmitted={() => setResumeSent(true)}
      />
    </>
  );
}

function SharePopup(props) {
  const { companyDetail, showDiv } = props;
  const { t } = useTranslation();

  const isDesktop = useMediaQuery("(min-width: 1280px)");

  const [triggerSnackbarFunc] = useTriggerSnackbar();
  const shareUrl = `${process.env.NEXT_PUBLIC_DOMAIN}/companies/${companyDetail?.slug}`;
  const title = t("companydetail.share_title", {
    company: companyDetail?.name,
  });

  async function handleCopyClipboard() {
    if (!navigator.clipboard) {
      // Clipboard API not available
      return;
    }
    const text = `${title}: ${shareUrl}`;
    try {
      await navigator.clipboard.writeText(text);
      handleEventTab("link");
      triggerSnackbarFunc({
        snackbarMessage: t("companydetail.link_copied"),
        severity: "success",
      });
    } catch (error) {
      triggerSnackbarFunc({
        snackbarMessage: t("companydetail.link_copy_failed"),
        severity: "error",
      });
    }
  }

  function handleEventTab(shareMethod) {
    sendTrackingEvent({
      event: "CE_click-share-cdp",
      "company-id": companyDetail?.id,
      item: shareMethod,
    });
  }

  return (
    <ShareButtonsContainer
      sx={!showDiv && { top: isDesktop ? "30px" : "30px" }}
    >
      <FacebookShareButton
        url={shareUrl}
        quote={title}
        id={"facebook-job-link-sharing-button"}
        onClick={() => handleEventTab("facebook")}
      >
        <FacebookIcon size={32} round />
      </FacebookShareButton>
      <TwitterShareButton
        url={shareUrl}
        aria-label={title}
        id={"twitter-job-link-sharing-button"}
        onClick={() => handleEventTab("X")}
      >
        <TwitterIcon size={32} round />
      </TwitterShareButton>
      <LinkedinShareButton
        url={shareUrl}
        summary={title}
        id={"linkedin-job-link-sharing-button"}
        onClick={() => handleEventTab("linkedin")}
      >
        <LinkedinIcon size={32} round />
      </LinkedinShareButton>
      <WhatsappShareButton
        url={shareUrl}
        aria-label={title}
        separator=": "
        id={"whatsapp-job-link-sharing-button"}
        onClick={() => handleEventTab("whatsapp")}
      >
        <WhatsappIcon size={32} round />
      </WhatsappShareButton>
      <EmailShareButton
        url={shareUrl}
        subject={title}
        id={"email-job-link-sharing-button"}
        onClick={() => handleEventTab("email")}
      >
        <EmailIcon size={32} round />
      </EmailShareButton>
      <ClipboardContainer>
        <LinkIconStyled onClick={handleCopyClipboard} />
      </ClipboardContainer>
    </ShareButtonsContainer>
  );
}

export default CompanyNavigation;
